import React, { useState } from "react";

type Choice = "same" | "different" | null;

export function VoirNestPasToujoursCroire() {
  const [choice, setChoice] = useState<Choice>(null);
  const [revealed, setRevealed] = useState(false);

  const blocks = [
    { id: "a", label: "Zone A" },
    { id: "b", label: "Zone B" },
  ];

  function handleReveal() {
    setRevealed(true);
  }

  function handleReset() {
    setChoice(null);
    setRevealed(false);
  }

  const isSameColor = true; // illusion: les deux blocs sont en réalité identiques
  const feedback =
    choice === null
      ? ""
      : choice === "same"
      ? "Bien vu ! Elles sont identiques, mais le contexte peut tromper."
      : "Elles semblent différentes, pourtant elles sont identiques : le contexte influence ta perception.";

  return (
    <div className="max-w-xl mx-auto rounded-2xl border p-6 shadow-sm bg-white dark:bg-slate-900 dark:border-slate-700 mt-8">
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Illusion de perception
          </p>
          <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
            Voir n’est pas toujours croire
          </h2>
        </div>
      </div>

      <p className="text-sm text-slate-700 dark:text-slate-200 mb-4">
        Ces deux zones te semblent-elles de la même couleur ou différentes ? Choisis, puis révèle la réponse.
      </p>

      <div className="flex justify-center items-center gap-4 my-6">
        {blocks.map((block) => (
          <div
            key={block.id}
            className="w-24 h-24 rounded-lg shadow-inner border border-slate-200 dark:border-slate-600 flex items-center justify-center"
            style={{
              background: "rgb(160, 160, 160)", // même couleur pour l'illusion
              boxShadow:
                block.id === "a"
                  ? "0 0 0 8px rgba(255,255,255,0.5)" // halo clair
                  : "0 0 0 8px rgba(30,41,59,0.4)", // halo sombre
            }}
          >
            <span className="text-xs text-slate-800 dark:text-slate-100">
              {block.label}
            </span>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-2">
        <button
          type="button"
          onClick={() => setChoice("same")}
          className={`rounded-lg border px-3 py-2 text-sm text-left transition ${
            choice === "same"
              ? "border-blue-500 bg-blue-50 text-blue-700 dark:border-blue-500 dark:bg-blue-900/40 dark:text-blue-100"
              : "border-slate-200 bg-white text-slate-700 hover:border-blue-200 hover:bg-blue-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:hover:border-blue-500/50 dark:hover:bg-slate-800/80"
          }`}
        >
          Même couleur
        </button>
        <button
          type="button"
          onClick={() => setChoice("different")}
          className={`rounded-lg border px-3 py-2 text-sm text-left transition ${
            choice === "different"
              ? "border-blue-500 bg-blue-50 text-blue-700 dark:border-blue-500 dark:bg-blue-900/40 dark:text-blue-100"
              : "border-slate-200 bg-white text-slate-700 hover:border-blue-200 hover:bg-blue-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:hover:border-blue-500/50 dark:hover:bg-slate-800/80"
          }`}
        >
          Couleur différente
        </button>
      </div>

      <div className="flex items-center justify-end gap-2 mt-4">
        <button
          type="button"
          onClick={handleReveal}
          disabled={choice === null}
          className={`px-4 py-1.5 rounded-full text-sm font-semibold ${
            choice === null
              ? "bg-slate-200 text-slate-500 cursor-not-allowed dark:bg-slate-800 dark:text-slate-500"
              : "bg-blue-600 text-white hover:bg-blue-500"
          }`}
        >
          Valider et révéler
        </button>
        <button
          type="button"
          onClick={handleReset}
          className="px-3 py-1.5 rounded-full border border-slate-300 text-sm font-medium text-slate-700 bg-white hover:bg-slate-50 dark:border-slate-600 dark:text-slate-100 dark:bg-slate-800 dark:hover:bg-slate-700"
        >
          Rejouer
        </button>
      </div>

      {revealed && (
        <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-300/50 dark:bg-amber-900/20 dark:text-amber-100">
          <p className="font-semibold">
            En réalité, ces deux zones sont de la même couleur !
          </p>
          <p className="text-xs mt-1 text-amber-900/80 dark:text-amber-100/80">
            Notre cerveau compare les couleurs au contexte (lumière, ombre, halo), pas seulement aux pixels bruts. Le
            contraste autour de chaque zone trompe la perception.
          </p>
          {feedback && (
            <p className="text-xs mt-1 text-amber-900/80 dark:text-amber-100/80">
              {feedback}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
